import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Calendar, BookOpen, Users, Plus, Search, Badge as BadgeIcon, User, GraduationCap, Star, Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PhaseSection } from '../lecturer/PhaseSection';
import { usePhaseManagement } from '@/hooks/usePhaseManagement';
import { Badge } from "@/components/ui/badge";
import { fetchAllGroupsData, exportElogToPDF, exportElogToExcel } from '@/utils/elogExport';

interface Group {
  id: string;
  name: string;
  lecturer_id: string;
  lecturer_name?: string;
  lecturer_color?: string;
  secondary_lecturers?: string[];
}

interface GroupLecturer {
  id: string;
  name: string;
  color: string;
  isPrimary: boolean;
  lessonCounts: { [phaseId: string]: number };
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
}

interface Phase {
  id: string;
  group_id: string;
  name: string;
  phase_number: number;
  created_at: string;
  updated_at: string;
}

export const AdminElectronicJournal = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [newPhaseName, setNewPhaseName] = useState('');
  const [showAddPhase, setShowAddPhase] = useState(false);
  const [groupLecturers, setGroupLecturers] = useState<GroupLecturer[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      toast({
        title: "\u0531\u0580\u057F\u0561\u0570\u0561\u0576\u0578\u0582\u0574",
        description: "\u054F\u057E\u0575\u0561\u056C\u0576\u0565\u0580\u0568 \u0562\u0565\u057C\u0576\u057E\u0578\u0582\u0574 \u0565\u0576..."
      });
      const allData = await fetchAllGroupsData();
      if (allData.length === 0) {
        toast({
          title: "\u054D\u056D\u0561\u056C",
          description: "\u053D\u0574\u0562\u0565\u0580 \u0579\u0565\u0576 \u0563\u057F\u0576\u057E\u0565\u056C",
          variant: "destructive"
        });
        return;
      }
      await exportElogToPDF(allData);
      toast({
        title: "\u0540\u0561\u057B\u0578\u0572\u0578\u0582\u0569\u0575\u0578\u0582\u0576",
        description: "PDF \u0586\u0561\u0575\u056C\u0568 \u0570\u0561\u057B\u0578\u0572\u0578\u0582\u0569\u0575\u0561\u0574\u0562 \u0576\u0565\u0580\u0562\u0565\u057C\u0576\u057E\u0565\u0581"
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "\u054D\u056D\u0561\u056C",
        description: "\u0549\u0570\u0561\u057B\u0578\u0572\u057E\u0565\u0581 \u0561\u0580\u057F\u0561\u0570\u0561\u0576\u0565\u056C \u0586\u0561\u0575\u056C\u0568",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      toast({
        title: "\u0531\u0580\u057F\u0561\u0570\u0561\u0576\u0578\u0582\u0574",
        description: "\u054F\u057E\u0575\u0561\u056C\u0576\u0565\u0580\u0568 \u0562\u0565\u057C\u0576\u057E\u0578\u0582\u0574 \u0565\u0576..."
      });
      const allData = await fetchAllGroupsData();
      if (allData.length === 0) {
        toast({
          title: "\u054D\u056D\u0561\u056C",
          description: "\u053D\u0574\u0562\u0565\u0580 \u0579\u0565\u0576 \u0563\u057F\u0576\u057E\u0565\u056C",
          variant: "destructive"
        });
        return;
      }
      await exportElogToExcel(allData);
      toast({
        title: "\u0540\u0561\u057B\u0578\u0572\u0578\u0582\u0569\u0575\u0578\u0582\u0576",
        description: "Excel \u0586\u0561\u0575\u056C\u0568 \u0570\u0561\u057B\u0578\u0572\u0578\u0582\u0569\u0575\u0561\u0574\u0562 \u0576\u0565\u0580\u0562\u0565\u057C\u0576\u057E\u0565\u0581"
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "\u054D\u056D\u0561\u056C",
        description: "\u0549\u0570\u0561\u057B\u0578\u0572\u057E\u0565\u0581 \u0561\u0580\u057F\u0561\u0570\u0561\u0576\u0565\u056C \u0586\u0561\u0575\u056C\u0568",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };
  
  const {
    phases,
    loading: phasesLoading,
    fetchPhases,
    addPhase,
    updatePhase,
    deletePhase
  } = usePhaseManagement();

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (selectedGroupId) {
      fetchGroupStudents();
      fetchPhases(selectedGroupId);
      fetchGroupLecturersData();
    }
  }, [selectedGroupId, fetchPhases]);

  useEffect(() => {
    if (selectedGroupId && phases.length > 0) {
      fetchGroupLecturersData();
    }
  }, [phases, selectedGroupId]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = students.filter(student =>
        `${student.first_name} ${student.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStudents(filtered);
    } else {
      setFilteredStudents(students);
    }
  }, [searchTerm, students]);

  const fetchGroups = async () => {
    try {
      const { data: groupsData, error: groupsError } = await supabase
        .from('groups')
        .select('id, name, lecturer_id, secondary_lecturers')
        .order('name');

      if (groupsError) throw groupsError;

      if (!groupsData || groupsData.length === 0) {
        setGroups([]);
        return;
      }

      // Get lecturer info for each group (primary + secondary)
      const allLecturerIds = new Set<string>();
      groupsData.forEach(group => {
        if (group.lecturer_id) allLecturerIds.add(group.lecturer_id);
        if (group.secondary_lecturers) {
          group.secondary_lecturers.forEach(id => allLecturerIds.add(id));
        }
      });
      const lecturerIds = Array.from(allLecturerIds);
      
      const { data: lecturerProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', lecturerIds);

      if (profilesError) throw profilesError;

      // Get lecturer colors
      const lecturerColors: { [key: string]: string } = {};
      for (const lecturerId of lecturerIds) {
        try {
          const { data: colorData, error: colorError } = await supabase
            .rpc('get_lecturer_color', { lecturer_uuid: lecturerId });
          
          if (!colorError && colorData) {
            lecturerColors[lecturerId] = colorData;
          } else {
            lecturerColors[lecturerId] = 'bg-muted';
          }
        } catch (colorError) {
          lecturerColors[lecturerId] = 'bg-muted';
        }
      }

      const groupsWithLecturers = groupsData.map(group => {
        const lecturer = lecturerProfiles?.find(p => p.id === group.lecturer_id);
        let lecturerName = 'Անհայտ դասախոս';
        
        if (lecturer) {
          const firstName = lecturer.first_name?.trim() || '';
          const lastName = lecturer.last_name?.trim() || '';
          
          if (firstName && lastName) {
            lecturerName = `${firstName} ${lastName}`;
          } else if (firstName) {
            lecturerName = firstName;
          } else if (lastName) {
            lecturerName = lastName;
          } else if (lecturer.email) {
            lecturerName = lecturer.email.split('@')[0];
          }
        }

        return {
          ...group,
          lecturer_name: lecturerName,
          lecturer_color: lecturerColors[group.lecturer_id] || 'bg-muted',
          secondary_lecturers: group.secondary_lecturers || []
        };
      });

      setGroups(groupsWithLecturers);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել խմբերը",
        variant: "destructive"
      });
    }
  };

  const fetchGroupStudents = async () => {
    if (!selectedGroupId) return;

    try {
      const { data: groupStudentData, error: groupError } = await supabase
        .from('group_students')
        .select('student_id')
        .eq('group_id', selectedGroupId);

      if (groupError) throw groupError;

      if (!groupStudentData || groupStudentData.length === 0) {
        setStudents([]);
        return;
      }

      const studentIds = groupStudentData.map(item => item.student_id);

      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', studentIds);

      if (profilesError) throw profilesError;

      setStudents(profilesData || []);
    } catch (error) {
      console.error('Error in fetchGroupStudents:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել ուսանողներին",
        variant: "destructive"
      });
    }
  };

  const fetchGroupLecturersData = async () => {
    if (!selectedGroupId || phases.length === 0) return;

    try {
      const selectedGroup = groups.find(g => g.id === selectedGroupId);
      if (!selectedGroup) return;

      const allLecturerIds = new Set<string>();
      if (selectedGroup.lecturer_id) allLecturerIds.add(selectedGroup.lecturer_id);
      selectedGroup.secondary_lecturers?.forEach(id => allLecturerIds.add(id));

      const lecturerIds = Array.from(allLecturerIds);
      if (lecturerIds.length === 0) return;

      // Get lecturer profiles
      const { data: lecturerProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', lecturerIds);

      if (profilesError) throw profilesError;

      // Get lecturer colors
      const lecturerColors: { [key: string]: string } = {};
      for (const lecturerId of lecturerIds) {
        try {
          const { data: colorData, error: colorError } = await supabase
            .rpc('get_lecturer_color', { lecturer_uuid: lecturerId });
          
          if (!colorError && colorData) {
            lecturerColors[lecturerId] = colorData;
          } else {
            lecturerColors[lecturerId] = 'bg-muted';
          }
        } catch (colorError) {
          lecturerColors[lecturerId] = 'bg-muted';
        }
      }

      // Get lesson counts per lecturer per phase
      const { data: lessonsData, error: lessonsError } = await supabase
        .from('lessons')
        .select('lecturer_id, marked_by_lecturer_id, phase_id, status, lesson_date, topic')
        .eq('group_id', selectedGroupId)
        .in('phase_id', phases.map(p => p.id));

      if (lessonsError) throw lessonsError;

      console.log('Lessons data for counting:', lessonsData);
      console.log('Phases for counting:', phases.map(p => ({ id: p.id, name: p.name })));

      // Build lecturer data with lesson counts
      const lecturerDataMap = lecturerIds.map(lecturerId => {
        const lecturer = lecturerProfiles?.find(p => p.id === lecturerId);
        let lecturerName = 'Անհայտ դասախոս';
        
        if (lecturer) {
          const firstName = lecturer.first_name?.trim() || '';
          const lastName = lecturer.last_name?.trim() || '';
          
          if (firstName && lastName) {
            lecturerName = `${firstName} ${lastName}`;
          } else if (firstName) {
            lecturerName = firstName;
          } else if (lastName) {
            lecturerName = lastName;
          }
        }

        // Count lessons for this lecturer in each phase
        const lessonCounts: { [phaseId: string]: number } = {};
        phases.forEach(phase => {
          const phaseId = phase.id;
          
          // Count all lessons for this lecturer (either as assigned lecturer or marked by)
          const relevantLessons = lessonsData?.filter(lesson => {
            return lesson.phase_id === phaseId && 
                   (lesson.lecturer_id === lecturerId || lesson.marked_by_lecturer_id === lecturerId);
          }) || [];
          
          lessonCounts[phaseId] = relevantLessons.length;
        });

        return {
          id: lecturerId,
          name: lecturerName,
          color: lecturerColors[lecturerId] || 'bg-muted',
          isPrimary: lecturerId === selectedGroup.lecturer_id,
          lessonCounts
        };
      });

      setGroupLecturers(lecturerDataMap);
    } catch (error) {
      console.error('Error fetching group lecturers data:', error);
    }
  };

  const handleAddPhase = async () => {
    if (!newPhaseName.trim() || !selectedGroupId) return;

    const success = await addPhase(selectedGroupId, newPhaseName.trim());
    if (success) {
      setNewPhaseName('');
      setShowAddPhase(false);
    }
  };

  const handleDeletePhase = async (phaseId: string) => {
    return await deletePhase(phaseId, selectedGroupId);
  };

  const handleUpdatePhase = async (phaseId: string, updates: Partial<Phase>) => {
    const success = await updatePhase(phaseId, updates);
    if (success) {
      await fetchPhases(selectedGroupId);
    }
    return success;
  };

  const selectedGroup = groups.find(g => g.id === selectedGroupId);

  return (
    <div className="space-y-4 md:space-y-6 w-full max-w-full overflow-hidden">
      <Card className="border-border/70 bg-card/80">
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <CardTitle className="text-muted-foreground flex items-center gap-2 text-lg md:text-xl">
            <BookOpen className="h-5 w-5" />
            {"\u0537\u056C\u0565\u056F\u057F\u0580\u0578\u0576\u0561\u0575\u056B\u0576 \u0574\u0561\u057F\u0575\u0561\u0576 - \u0531\u0564\u0574\u056B\u0576\u056B \u057F\u0565\u057D\u0561\u0580\u0561\u0576"}
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleExportPDF}
              disabled={isExporting}
              variant="outline"
              size="sm"
              className="text-primary border-border/70 hover:bg-primary/10"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              PDF
            </Button>
            <Button
              onClick={handleExportExcel}
              disabled={isExporting}
              variant="outline"
              size="sm"
              className="text-primary border-border/70 hover:bg-blue-400/20"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4 mr-2" />
              )}
              Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Group Selection */}
          <div className="w-full">
            <Label htmlFor="groupSelect" className="text-muted-foreground mb-2 block text-sm">
              Ընտրեք խումբը
            </Label>
            <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
              <SelectTrigger className="bg-muted border-border/70 text-foreground w-full">
                <SelectValue placeholder="Ընտրեք խումբը" />
              </SelectTrigger>
              <SelectContent className="bg-muted border-border/70">
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id} className="text-foreground">
                    {group.name} ({group.lecturer_name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selected Group Info with Lecturers Overview */}
          {selectedGroup && groupLecturers.length > 0 && (
            <div className="bg-muted/50 rounded-lg p-4 border border-border/70">
              <div className="flex items-center gap-3 mb-4">
                <GraduationCap className="h-5 w-5 text-primary" />
                <h3 className="text-muted-foreground font-semibold text-lg">{selectedGroup.name}</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 text-primary" />
                  <span className="text-muted-foreground text-sm font-semibold">
                    Խմբի դասախոսներ և նրանց ավարտած դասերը փուլերի համաձայն:
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/70">
                        <TableHead className="text-muted-foreground min-w-[200px]">
                          Դասախոս
                        </TableHead>
                        {phases.map(phase => (
                          <TableHead key={phase.id} className="text-muted-foreground text-center min-w-[120px]">
                            {phase.name}
                          </TableHead>
                        ))}
                        <TableHead className="text-muted-foreground text-center min-w-[100px]">
                          Ընդամենը
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupLecturers.map(lecturer => {
                        const totalLessons = Object.values(lecturer.lessonCounts).reduce((sum, count) => sum + count, 0);
                        
                        return (
                          <TableRow key={lecturer.id} className="border-border/70">
                            <TableCell className="font-mono">
                              <div className="flex items-center gap-3">
                                <div className={`w-3 h-3 rounded-full ${lecturer.color.replace('bg-', 'bg-')}`}></div>
                                <div className="flex flex-col">
                                  <span className="text-foreground font-medium">{lecturer.name}</span>
                                  <div className="flex items-center gap-1">
                                    {lecturer.isPrimary ? (
                                      <Badge className="text-xs bg-warning/10 border-warning text-warning">
                                        <Star className="h-3 w-3 mr-1" />
                                        Հիմնական
                                      </Badge>
                                    ) : (
                                      <Badge className="text-xs bg-info/10 border-info text-info">
                                        Երկրորդական
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            {phases.map(phase => (
                              <TableCell key={phase.id} className="text-center">
                                <Badge 
                                  variant="outline" 
                                  className={`${
                                    lecturer.lessonCounts[phase.id] > 0 
                                      ? 'bg-green-500/10 border-border/70 text-muted-foreground' 
                                      : 'bg-muted/50 border-border text-muted-foreground'
                                  }`}
                                >
                                  {lecturer.lessonCounts[phase.id]} դաս
                                </Badge>
                              </TableCell>
                            ))}
                            <TableCell className="text-center">
                              <Badge 
                                className={`${
                                  totalLessons > 0 
                                    ? 'bg-blue-500/10 border-blue-400 text-muted-foreground' 
                                    : 'bg-muted/50 border-border text-muted-foreground'
                                }`}
                              >
                                {totalLessons} դաս
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {selectedGroupId && students.length > 0 && (
            <div className="space-y-4 p-3 md:p-4 bg-muted/50 rounded-lg border border-border/70">
              {/* Search Students */}
              <div className="w-full">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-primary" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-muted border-border/70 text-foreground w-full"
                    placeholder="Որոնել ուսանող..."
                  />
                </div>
              </div>

              {/* Controls Section - Centered layout */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3">
                  <div className="flex justify-center">
                    <Badge variant="outline" className="text-muted-foreground border-border/70">
                      <Users className="h-3 w-3 mr-1" />
                      Ուսանողներ: {filteredStudents.length}
                    </Badge>
                  </div>
                  
                  <div className="flex justify-center w-full">
                    {!showAddPhase ? (
                      <Button
                        onClick={() => setShowAddPhase(true)}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground w-full max-w-sm"
                        size="sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Նոր փուլ ավելացնել
                      </Button>
                    ) : (
                      <div className="flex flex-col gap-3 w-full max-w-sm">
                        <Input
                          value={newPhaseName}
                          onChange={(e) => setNewPhaseName(e.target.value)}
                          placeholder="Փուլի անուն..."
                          className="bg-muted border-border/70 text-foreground w-full"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddPhase();
                            if (e.key === 'Escape') {
                              setShowAddPhase(false);
                              setNewPhaseName('');
                            }
                          }}
                        />
                        <div className="flex flex-col gap-2 w-full">
                          <Button
                            onClick={handleAddPhase}
                            disabled={!newPhaseName.trim() || phasesLoading}
                            className="bg-primary hover:bg-primary/90 w-full"
                            size="sm"
                          >
                            Ավելացնել
                          </Button>
                          <Button
                            onClick={() => {
                              setShowAddPhase(false);
                              setNewPhaseName('');
                            }}
                            variant="outline"
                            className="text-muted-foreground border-border w-full"
                            size="sm"
                          >
                            Չեղարկել
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Phases Section */}
      {selectedGroupId && students.length > 0 && (
        <div className="space-y-4 md:space-y-6 w-full max-w-full">
          {/* Admin note */}
          <Card className="bg-blue-900/20 border-border/70">
            <CardContent className="py-3">
              <div className="flex items-center gap-2">
                <BadgeIcon className="h-4 w-4 text-primary" />
                <p className="text-muted-foreground text-sm">
                  Ադմինի տեսարան: Գուները ցույց են տալիս, թե որ դասախոսի կողմից է լրացվել փուլը
                </p>
              </div>
            </CardContent>
          </Card>

          {phases.length > 0 ? (
            phases.map((phase) => (
              <div key={phase.id} className="w-full max-w-full overflow-hidden">
                {/* Phase header with lecturer indicator */}
                <div className="mb-2 flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${selectedGroup?.lecturer_color}`}></div>
                  <span className="text-muted-foreground text-sm">
                    {phase.name} - {selectedGroup?.lecturer_name}
                  </span>
                </div>
                <PhaseSection
                  phase={phase}
                  students={filteredStudents}
                  onDeletePhase={handleDeletePhase}
                  onUpdatePhase={handleUpdatePhase}
                  isLoading={phasesLoading}
                />
              </div>
            ))
          ) : (
            <Card className="border-border/70 bg-card/80">
              <CardContent className="py-8">
                <div className="text-center flex flex-col items-center">
                  <Calendar className="h-12 w-12 text-primary mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4 text-sm md:text-base">Այս խմբում փուլեր չկան</p>
                  <div className="flex justify-center w-full">
                    <Button
                      onClick={() => setShowAddPhase(true)}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground w-full max-w-sm"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Առաջին փուլը ստեղծել
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Empty States */}
      {selectedGroupId && students.length === 0 && (
        <Card className="border-border/70 bg-card/80">
          <CardContent className="py-8">
            <div className="text-center">
              <Users className="h-12 w-12 text-primary mx-auto mb-4" />
              <p className="text-muted-foreground text-sm md:text-base">Այս խմբում ուսանողներ չկան</p>
            </div>
          </CardContent>
        </Card>
      )}

      {!selectedGroupId && (
        <Card className="border-border/70 bg-card/80">
          <CardContent className="py-8">
            <div className="text-center">
              <Calendar className="h-12 w-12 text-primary mx-auto mb-4" />
              <p className="text-muted-foreground text-sm md:text-base">Ընտրեք խումբ մատյանը տեսնելու համար</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};